import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'

import AttendanceV2DataTable, {ColumnDef, INITIAL_VISIBLE_ROWS} from './AttendanceV2DataTable'

const LOAD_MORE_BATCH = 8

type AttendanceV2ListSectionProps = {
  title: string
  columns: ColumnDef[]
  rows: Record<string, unknown>[]
  theme: 'light' | 'dark'
}

/** One titled, load-more-capable list inside an attendance card. Long lists (e.g. 120 unmarked
 *  students) stay compact by batching rows the same way the generic result view does. */
const AttendanceV2ListSection: React.FC<AttendanceV2ListSectionProps> = ({
  title,
  columns,
  rows,
  theme,
}) => {
  const intl = useIntl()
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_ROWS)

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_ROWS)
  }, [rows.length])

  const visibleRows = useMemo(() => rows.slice(0, visibleCount), [rows, visibleCount])
  const showLoadMore = rows.length > INITIAL_VISIBLE_ROWS && visibleCount < rows.length
  const remaining = rows.length - visibleCount
  const showLess = visibleCount > INITIAL_VISIBLE_ROWS
  const collapsible = Math.min(
    LOAD_MORE_BATCH,
    Math.min(visibleCount, rows.length) - INITIAL_VISIBLE_ROWS
  )

  const handleLoadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + LOAD_MORE_BATCH, rows.length))
  }, [rows.length])

  const handleShowLess = useCallback(() => {
    setVisibleCount((prev) =>
      Math.max(Math.min(prev, rows.length) - LOAD_MORE_BATCH, INITIAL_VISIBLE_ROWS)
    )
  }, [rows.length])

  if (!rows.length) return null

  const titleColor = theme === 'dark' ? '#ffffff' : '#212529'

  return (
    <div className='mb-2'>
      {title ? (
        <div className='fw-semibold mb-1' style={{fontSize: '0.85rem', color: titleColor}}>
          {title}
        </div>
      ) : null}
      <AttendanceV2DataTable
        columns={columns}
        rows={visibleRows}
        totalRows={rows.length}
        theme={theme}
      />
      {showLoadMore || showLess ? (
        <div className='d-flex justify-content-end gap-1 mt-2'>
          {showLess ? (
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={handleShowLess}
              style={{
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                padding: '0.35rem 0.6rem',
              }}
            >
              {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.SHOW_LESS'})}
              {` (${collapsible})`}
            </button>
          ) : null}
          {showLoadMore ? (
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={handleLoadMore}
              style={{
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
                padding: '0.35rem 0.6rem',
              }}
            >
              {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.LOAD_MORE'})}
              {remaining > LOAD_MORE_BATCH ? ` (${LOAD_MORE_BATCH})` : ` (${remaining})`}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default AttendanceV2ListSection
